/**
 * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
 * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
 *
 * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
 */

import { getTimersStore } from './timersStore.js';

export const guidedNavigationKinds = {
    timer: 'timer',
    sequence: 'sequence'
};

const itemCategories = {
    manualNavDisable: 'x-tao-manual-nav-disable',
    sequenceEndedNavNext: 'x-tao-sequence-ended-nav-next'
};

function hasGuidedNavigationCategories(currentTestPart, currentItem) {
    return Boolean(
        currentTestPart?.isLinear &&
            currentItem?.categories?.includes(itemCategories.manualNavDisable) &&
            currentItem?.categories?.includes(itemCategories.sequenceEndedNavNext)
    );
}

/**
 * Check if navigation needs to be disabled giving min max timers
 * @param {string} serviceCallId
 * @param {object} currentTestPart
 * @param {string} itemIdentifier
 * @returns {boolean}
 */
export function isNavigationDisabledByTimers(serviceCallId, currentTestPart, itemIdentifier) {
    if (!serviceCallId || !currentTestPart || !itemIdentifier) {
        return false;
    }
    const timersStore = getTimersStore(serviceCallId);
    if (!timersStore) {
        return false;
    }
    const areTimersEqual = timersStore.isMinMaxEqual(itemIdentifier);
    //leave nav disabled only if timers are equal and test is linear otherwise enable it
    return areTimersEqual && currentTestPart.isLinear;
}

/**
 * Get the guided-navigation state for the current item.
 * Timer-driven guidance disables forward navigation on min=max timed items.
 * Sequence-driven guidance applies when both authored categories are present.
 * @param {string} serviceCallId
 * @param {object} currentTestPart
 * @param {object} currentItem
 * @returns {{isDisabled: boolean, kind: string|null, isSequenceDriven: boolean}}
 */
export function getGuidedNavigationState(serviceCallId, currentTestPart, currentItem) {
    if (!currentTestPart || !currentItem) {
        return {
            isDisabled: false,
            kind: null,
            isSequenceDriven: false
        };
    }

    const isTimerDriven = isNavigationDisabledByTimers(serviceCallId, currentTestPart, currentItem.id);
    const isSequenceDriven = hasGuidedNavigationCategories(currentTestPart, currentItem);
    let kind = null;

    if (isTimerDriven) {
        kind = guidedNavigationKinds.timer;
    } else if (isSequenceDriven) {
        kind = guidedNavigationKinds.sequence;
    }

    return {
        isDisabled: isTimerDriven || isSequenceDriven,
        kind,
        isSequenceDriven
    };
}
